import { AuthContext, useIsHydrated } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Button, Confirm, ErrorMessage, Heading, Input } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useValidations, useSubmitValidation } from '../../../hooks';
import { PasswordEyeIcon } from '../../atoms';

interface DeleteAccountState {
    password: string;
}

export const DeleteAccountSection = () => {
    const { t } = useTranslation();
    const { updateAuthState } = useContext(AuthContext);
    const isHydrated = useIsHydrated();
    const [passwordType, setPasswordType] = useState('password');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const {
        watch,
        register,
        trigger,
        getValues,
        reset,
        formState: { errors }
    } = useForm<DeleteAccountState>({ mode: 'onSubmit', reValidateMode: 'onSubmit' });
    const validate = useSubmitValidation({ watch, trigger });

    const { passwordRequired } = useValidations();

    const deleteAccountMutation = useMutation(OptionsApi.account.deleteAccount());

    const handleDeleteClick = async () => {
        const isValid = await validate(['password']);
        if (!isValid) return;
        setConfirmOpen(true);
    };

    const handleConfirm = () => {
        const { password } = getValues();
        deleteAccountMutation.mutate(password, {
            onSuccess: () => {
                // Hard-reload to '/' rather than client-side navigate. The homepage's
                // useSuspenseQuery for /webinars/my-upcoming reads isAuthenticated at render time
                // and would otherwise fire the auth-required query *before* AuthContext propagates
                // the cleared state — sending the just-deleted account's token to the server, which
                // 401s. A fresh page load drops every cached query and in-flight fetch tied to the
                // deleted account; AuthProvider re-initialises with no token (the updateAuthState
                // call above clears localStorage), and the homepage picks its no-session branch.
                updateAuthState({ isAuthenticated: false, token: null, mainAccount: null, activeAccount: null, managedAccounts: [], rights: [] });
                globalThis.window?.location.assign('/');
            }
        });
    };

    return (
        <div className="bg-warning-light rounded-b-lg flex flex-col -mt-4 mb-4 md:mb-6 lg:mb-8 pt-8 px-4 md:px-8 pb-4 md:pb-6">
            <Heading variant="h2" classNames="pb-2">
                {t('Account.DeleteAccount.PageHeading')}
            </Heading>
            <p className="mb-6">{t('Account.DeleteAccount.Warning')}</p>

            {deleteAccountMutation.error && <ErrorMessage apiError={deleteAccountMutation.error} classNames="mb-4" />}

            <div className="max-w-lg">
                <Input
                    data-testid="delete-account-password"
                    type={passwordType}
                    label={t('Account.Label.CurrentPassword')}
                    {...register('password', passwordRequired)}
                    errorMessage={errors.password?.message}
                    autoComplete="current-password"
                >
                    <PasswordEyeIcon passwordType={passwordType} onClick={() => setPasswordType((t) => (t === 'password' ? 'text' : 'password'))} />
                </Input>

                <Button
                    data-testid="delete-account-button"
                    variant="warning"
                    isLoading={deleteAccountMutation.isPending}
                    disabled={!isHydrated}
                    onClick={handleDeleteClick}
                >
                    {t('Account.DeleteAccount.Button')}
                </Button>
            </div>

            <Confirm
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
                title={t('Account.DeleteAccount.Confirm.Title')}
                closeText={t('Generic.Button.Cancel')}
                confirmText={t('Account.DeleteAccount.Confirm.Button')}
                confirmTestId="delete-account-confirm"
            >
                <p>{t('Account.DeleteAccount.Confirm.Body')}</p>
            </Confirm>
        </div>
    );
};
